import React from "react";
import { asField } from "informed";
import moment from "moment";
import { isEmpty } from "lodash";

import { SimpleSelectInput } from "./index";
import validatehoc from "../Validator";
import GetItem from "../../../helpers/dataProviders/getItem";
import PostItem from "../../../helpers/dataProviders/postItem";
import parseAppKey from "../../../helpers/routeHelpers/parseAppKey";

class FormstackFormPicker extends React.Component {
	constructor(props) {
		super(props);
		this.appKey = parseAppKey();
		this.state = {
			// data
			allFormstackForms: [],
			formstackIntegrations: [],
			// oauth data
			oauthIntegrationId: null,
			oauthIntegrationName: null,
			accessToken: null,
			// form data
			formstackFormId: null,
			formstackFormJson: null,
			refreshedAt: null,
			// flags
			hasPickedOAuthIntegration: false,
			hasPickedFormstackForm: false,
			hasFormstackFormData: false,
			loading: false,
			// errors?
			errorGettingIntegrations: null,
			errorGettingFormstackForms: null,
			errorGettingFormData: null,
		};
	}

	componentDidMount() {
		let { initialValue } = this.props;
		let initialState = {};

		this.setState({ loading: true }, async () => {
			const formstackIntegrations = await this.getFormstackIntegrations();
			initialState = {
				...initialState,
				formstackIntegrations,
			};

			if (!isEmpty(initialValue)) {
				Object.keys(initialValue).forEach(property => {
					initialState[property] = initialValue[property];
				});
				if (initialValue.oauthIntegrationId) {
					// we can use this ID to get all of the formstack forms
					initialState.hasPickedOAuthIntegration = true;

					let allFormstackFormsResponse = await this.getAllFormstackForms(
						initialValue.oauthIntegrationId,
						formstackIntegrations
					);
					initialState = {
						...initialState,
						...allFormstackFormsResponse,
					};
				}
				if (initialValue.formstackFormId) {
					// we already have a form, which should assume
					// - form ID (that's how we got in here)
					// - formData (JSON string of the form)
					// - refreshed at
					initialState.hasPickedFormstackForm = true;

					let formDataResponse = null;
					if (initialValue.formData && initialValue.refreshedAt) {
						// if we actually have everything we need (formData and refreshedAt), use that
						initialState = {
							...initialState,
							refreshedAt: initialValue.refreshedAt,
							formstackFormJson: initialValue.formData,
						};
					} else {
						// otherwise, we need new form JSON and refreshedAt time
						formDataResponse = await this.getFormData(
							initialValue.formstackFormId,
							initialState.accessToken
						);
						initialState = {
							...initialState,
							...formDataResponse,
						};
					}
				}
			}

			this.setState({ loading: false, ...initialState });
		});
	}

	async componentDidUpdate(prevProps, prevState) {
		const {
			accessToken,
			formstackFormId,
			formstackIntegrations,
			oauthIntegrationId,
			hasPickedOAuthIntegration,
			hasPickedFormstackForm,
			loading,
		} = this.state;

		const loadingChanged = prevState.loading !== this.state.loading;

		if (loadingChanged && loading) {
			if (hasPickedOAuthIntegration && !hasPickedFormstackForm) {
				const response = await this.getAllFormstackForms(oauthIntegrationId, formstackIntegrations);
				this.setState({ ...response, loading: false });
			}
			if (hasPickedFormstackForm) {
				const response = await this.getFormData(formstackFormId, accessToken);
				if (response.refreshedAt) this.updateFormFieldValue("refreshedAt", response.refreshedAt);
				if (response.formstackFormJson)
					this.updateFormFieldValue("formData", JSON.stringify(response.formstackFormJson));
				this.setState({ ...response, hasFormstackFormData: true, loading: false });
			}
		}
	}

	getFormstackIntegrations = async () => {
		let response = null;
		try {
			const data = await GetItem(`/apps/${this.appKey}/oauth`);
			response = data.data.filter(i => i.moduleName === "Formstack-integration");
		} catch (error) {
			response = { errorGettingIntegrations: "Error getting Formstack integrations" };
			console.warn(response.errorGettingIntegrations, error);
		}
		return response;
	};

	getAccessToken = (oauthIntegrationId, formstackIntegrations) => {
		const integration = formstackIntegrations.find(i => i.id === oauthIntegrationId);
		return integration ? integration.accessToken : false;
	};

	getAllFormstackForms = async (oauthIntegrationId, formstackIntegrations) => {
		let response = null;
		const accessToken = this.getAccessToken(oauthIntegrationId, formstackIntegrations);

		if (accessToken) {
			const postData = {
				url: "https://www.formstack.com/api/v2/form.json",
				headers: {
					Authorization: `Bearer ${accessToken}`,
				},
			};
			try {
				const data = await PostItem(`/oauth/providerData`, postData);
				const formstackResponse = JSON.parse(data.data.response);
				if (formstackResponse.total > 0) {
					const { forms: allFormstackForms } = formstackResponse;
					response = {
						allFormstackForms,
						accessToken,
					};
				} else {
					response = { errorGettingFormstackForms: "No Formstack forms found" };
					console.log(response.errorGettingFormstackForms);
				}
			} catch (error) {
				response = { errorGettingFormstackForms: "Error getting Formstack forms" };
				console.warn(response.errorGettingFormstackForms, error);
			}
		} else {
			response = { errorGettingFormstackForms: "Integration not found" };
			console.warn(response.errorGettingFormstackForms);
		}

		return response;
	};

	getFormData = async (formstackFormId, accessToken) => {
		const postData = {
			url: `https://www.formstack.com/api/v2/form/${formstackFormId}.json`,
			headers: {
				Authorization: `Bearer ${accessToken}`,
			},
		};
		let response = null;
		try {
			const { data } = await PostItem(`/oauth/providerData`, postData);

			const formstackFormJson = JSON.parse(data.response);

			if (!isEmpty(formstackFormJson) && data.refreshedAt) {
				response = {
					formstackFormJson,
					refreshedAt: data.refreshedAt,
				};
			} else {
				response = { errorGettingFormData: "Empty form JSON returned" };
				console.warn(response.errorGettingFormData);
			}
		} catch (error) {
			response = { errorGettingFormData: "Error getting form data" };
			console.warn(response.errorGettingFormData, error);
		}

		return response;
	};

	changeOAuthIntegration = event => {
		const oauthIntegrationId = event.target.value;
		this.setState((state, props) => {
			this.updateFormFieldValue("oauthIntegrationId", oauthIntegrationId);
			if (state.oauthIntegrationId !== oauthIntegrationId) {
				// we changed integrations - clear data downstream in sequence
				this.deleteFormFieldValue("formstackFormId");
				this.deleteFormFieldValue("refreshedAt");
				this.deleteFormFieldValue("formData");
				return {
					oauthIntegrationId,
					hasPickedOAuthIntegration: true,
					// all formstack forms data
					allFormstackForms: [],
					accessToken: null,
					formstackFormId: null,
					hasPickedFormstackForm: false,
					// single form data
					refreshedAt: null,
					formstackFormJson: null,
					loading: true,
				};
			} else {
				return {
					oauthIntegrationId,
					hasPickedOAuthIntegration: true,
					loading: true,
				};
			}
		});
	};

	changeFormstackForm = event => {
		const formstackFormId = event.target.value;
		this.setState(state => {
			this.updateFormFieldValue("formstackFormId", formstackFormId);
			if (state.formstackFormId !== formstackFormId) {
				this.deleteFormFieldValue("refreshedAt");
				this.deleteFormFieldValue("formData");
				// we changed forms - clear data downstream in sequence
				return {
					formstackFormId,
					hasPickedFormstackForm: true,
					// single form data
					refreshedAt: null,
					formstackFormJson: null,
					loading: true,
				};
			} else {
				return { formstackFormId, hasPickedFormstackForm: true, loading: true };
			}
		});
	};

	refreshFormData = event => {
		event.preventDefault();
		this.setState({ formstackFormJson: null, loading: true });
	};

	updateFormFieldValue = (property, value) => {
		const { fieldApi } = this.props;

		let existingFieldValue = fieldApi.getValue();
		fieldApi.setValue({
			...existingFieldValue,
			[property]: value,
		});

		fieldApi.setTouched(true);
	};

	deleteFormFieldValue = property => {
		const { fieldApi } = this.props;

		let existingFieldValue = fieldApi.getValue();
		delete existingFieldValue[property];

		fieldApi.setValue({ ...existingFieldValue });
	};

	render() {
		const { fieldState } = this.props;
		const {
			allFormstackForms,
			formstackFormId,
			formstackFormJson,
			formstackIntegrations,
			oauthIntegrationId,
			refreshedAt,
			hasPickedOAuthIntegration,
			hasPickedFormstackForm,
			loading,
			errorGettingIntegrations,
			errorGettingFormstackForms,
			errorGettingFormData,
		} = this.state;

		const { error } = fieldState;
		const hasError = error ? " has-error" : "";

		const allFormstackFormsValues = allFormstackForms.map(i => ({
			key: i.id,
			value: i.id,
			label: i.name,
		}));
		const formstackIntegrationsValues = formstackIntegrations.map(i => ({ key: i.id, value: i.id, label: i.name }));

		const loadingOAuthIntegrations = loading && isEmpty(formstackIntegrations);
		const showIntegrationPicker = !errorGettingIntegrations && !isEmpty(formstackIntegrations);

		const loadingAllFormstackForms = hasPickedOAuthIntegration && loading && isEmpty(allFormstackForms);
		const showFormPicker = !errorGettingFormstackForms && hasPickedOAuthIntegration && !isEmpty(allFormstackForms);

		const loadingFormstackFormData = hasPickedFormstackForm && loading && isEmpty(formstackFormJson);
		const showLastRefreshed = !errorGettingFormData && hasPickedFormstackForm && !isEmpty(formstackFormJson);

		return (
			<div className={`input-wrap${hasError}`}>
				<div className="formstack-form-picker">
					{loadingOAuthIntegrations && <small>Loading integrations...</small>}
					{errorGettingIntegrations && <small>{errorGettingIntegrations}</small>}
					{showIntegrationPicker && (
						<SimpleSelectInput
							id="oauthIntegrationId"
							placeholder="Formstack Integration"
							values={formstackIntegrationsValues}
							value={oauthIntegrationId || ""}
							onChange={this.changeOAuthIntegration}
						/>
					)}
					{loadingAllFormstackForms && <small>Getting all forms from Formstack</small>}
					{errorGettingFormstackForms && <small>{errorGettingFormstackForms}</small>}
					{showFormPicker && (
						<React.Fragment>
							<SimpleSelectInput
								id="formstackFormId"
								placeholder="Formstack Forms"
								values={allFormstackFormsValues}
								value={formstackFormId || ""}
								onChange={this.changeFormstackForm}
							/>
							<div className="formstack-refreshed-info">
								{loadingFormstackFormData && <div className="loader" />}
								{errorGettingFormData && <small>Error getting form data</small>}
								{showLastRefreshed && (
									<React.Fragment>
										<span className="m-right">
											<i className="fas fa-check m-right" style={{ color: "#00c15b" }} />
											Form data fetched {moment(refreshedAt).format("YYYY/MM/DD")}
										</span>
										<a onClick={this.refreshFormData}>Refresh</a>
									</React.Fragment>
								)}
							</div>
						</React.Fragment>
					)}
					{error && <div className="error-message caption-text">{error}</div>}
				</div>
			</div>
		);
	}
}

const Wrapper = validatehoc(
	asField(({ fieldState, ...props }) => {
		return <FormstackFormPicker fieldState={fieldState} {...props} />;
	}),
	"formstack"
);

export default Wrapper;
